import { apiClient } from "../../lib/apiClient";
import type {
  ChatMessage,
  ChatMessagesResponse,
  GuildDetail,
  GuildsListResponse,
  UpdateGuildInput,
} from "./types";

export interface ListGuildsParams {
  q?: string;
  page?: number;
  limit?: number;
}

const base = (projectId: string) =>
  `/api/admin/projects/${encodeURIComponent(projectId)}/guilds`;

/** Fetch a page of guilds for a project. */
export async function listGuilds(
  projectId: string,
  params: ListGuildsParams = {},
): Promise<GuildsListResponse> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.page) qs.set("page", String(params.page));
  if (params.limit) qs.set("limit", String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  const r = await apiClient.get<GuildsListResponse>(`${base(projectId)}${suffix}`);
  return r.data;
}

/** Fetch one guild with its members. */
export async function getGuild(
  projectId: string,
  guildId: string,
): Promise<GuildDetail> {
  const r = await apiClient.get<GuildDetail>(
    `${base(projectId)}/${encodeURIComponent(guildId)}`,
  );
  return r.data;
}

/** PATCH a guild's settings. Last-write-wins. Returns the updated detail. */
export async function updateGuild(
  projectId: string,
  guildId: string,
  input: UpdateGuildInput,
): Promise<GuildDetail> {
  const r = await apiClient.patch<GuildDetail>(
    `${base(projectId)}/${encodeURIComponent(guildId)}`,
    input,
  );
  return r.data;
}

/** Change a member's role. role: "leader"→transfer, "officer"→promote,
 *  "member"→demote. Returns the updated guild detail. */
export async function updateMemberRole(
  projectId: string,
  guildId: string,
  playerId: string,
  role: "leader" | "officer" | "member",
): Promise<GuildDetail> {
  const r = await apiClient.patch<GuildDetail>(
    `${base(projectId)}/${encodeURIComponent(guildId)}/members/${encodeURIComponent(playerId)}`,
    { role },
  );
  return r.data;
}

/** Kick a member from the guild. */
export async function kickMember(
  projectId: string,
  guildId: string,
  playerId: string,
): Promise<void> {
  await apiClient.delete(
    `${base(projectId)}/${encodeURIComponent(guildId)}/members/${encodeURIComponent(playerId)}`,
  );
}

export interface ListMessagesParams {
  page?: number;
  limit?: number;
}

/** Fetch a page of a guild's chat history (newest-first from the server). */
export async function listMessages(
  projectId: string,
  guildId: string,
  params: ListMessagesParams = {},
): Promise<ChatMessagesResponse> {
  const qs = new URLSearchParams();
  if (params.page) qs.set("page", String(params.page));
  if (params.limit) qs.set("limit", String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  const r = await apiClient.get<ChatMessagesResponse>(
    `${base(projectId)}/${encodeURIComponent(guildId)}/messages${suffix}`,
  );
  return r.data;
}

/** Post a system message to the guild chat as the admin. */
export async function postMessage(
  projectId: string,
  guildId: string,
  body: string,
): Promise<ChatMessage> {
  const r = await apiClient.post<ChatMessage>(
    `${base(projectId)}/${encodeURIComponent(guildId)}/messages`,
    { body },
  );
  return r.data;
}

/** Hard-delete one chat message. */
export async function deleteMessage(
  projectId: string,
  guildId: string,
  messageId: string,
): Promise<void> {
  await apiClient.delete(
    `${base(projectId)}/${encodeURIComponent(guildId)}/messages/${encodeURIComponent(messageId)}`,
  );
}
